/**
 * Workforce Intelligence Governance Framework
 * 
 * Conceptual governance document outlining standards for WII measurement,
 * data ethics, AI validation, and institutional compliance.
 */

import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield, Scale, Eye, Lock, FileCheck, Users, Building2, Globe,
  CheckCircle, AlertTriangle, BookOpen, Target, Layers, ArrowRight,
  ClipboardCheck, Database, Brain, HeartHandshake, Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import BackNavigation from "@/components/BackNavigation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Governance Pillars
const GOVERNANCE_PILLARS = [
  {
    id: "measurement",
    icon: Scale,
    title: "Measurement Standards",
    description: "Standardized protocols for WII assessment, scoring, and benchmarking.",
    color: "#1E3A8A",
    principles: [
      {
        title: "Validated Assessment Instruments",
        description: "All WII assessments must be developed using validated psychometric methods and undergo regular reliability testing."
      },
      {
        title: "Consistent Scoring Methodology",
        description: "The Seven Forces scoring framework applies uniformly across all user types, with transparent weighting and calculation methods."
      },
      {
        title: "Benchmark Integrity",
        description: "Global and industry benchmarks must be derived from statistically significant sample sizes with documented methodology."
      },
      {
        title: "Version Control",
        description: "Assessment instruments are versioned, with clear migration paths when updates are made to ensure longitudinal comparability."
      }
    ]
  },
  {
    id: "data-ethics",
    icon: Lock,
    title: "Data Ethics & Privacy",
    description: "Ethical guidelines for collection, storage, and use of workforce intelligence data.",
    color: "#991B1B",
    principles: [
      {
        title: "Informed Consent",
        description: "Users must provide explicit consent before assessment data is collected, with clear explanation of how data will be used."
      },
      {
        title: "Data Minimization",
        description: "Only data necessary for WII calculation and user benefit is collected. No extraneous personal information is required."
      },
      {
        title: "Anonymization Standards",
        description: "Aggregate benchmark data is fully anonymized. Individual data is never shared without explicit user authorization."
      },
      {
        title: "Right to Access & Delete",
        description: "Users maintain full rights to access their data, export it, or request complete deletion at any time."
      }
    ]
  },
  {
    id: "ai-validation",
    icon: Brain,
    title: "AI Validation & Transparency",
    description: "Standards for AI capability assessment and transparent integration recommendations.",
    color: "#2563EB",
    principles: [
      {
        title: "Capability Verification",
        description: "AI tools in the Registry must have documented, verified capabilities with evidence of claimed functionality."
      },
      {
        title: "Bias Assessment",
        description: "All AI recommendations undergo bias review to ensure equitable outcomes across user demographics."
      },
      {
        title: "Explainable Recommendations",
        description: "AI-driven suggestions (tool matches, opportunity recommendations) must be explainable with clear reasoning."
      },
      {
        title: "Human Oversight",
        description: "AI recommendations are advisory. Users and institutions maintain decision authority over workforce development paths."
      }
    ]
  },
  {
    id: "institutional",
    icon: Building2,
    title: "Institutional Compliance",
    description: "Framework for organizational and governmental adoption of WII standards.",
    color: "#059669",
    principles: [
      {
        title: "Accreditation Standards",
        description: "Organizations using WII for workforce assessment must meet accreditation requirements for fair and ethical application."
      },
      {
        title: "Usage Boundaries",
        description: "WII may not be used as the sole criterion for employment decisions. It is a development tool, not a selection filter."
      },
      {
        title: "Audit Requirements",
        description: "Institutional deployments are subject to periodic governance audits to ensure compliance with ethical standards."
      },
      {
        title: "Dispute Resolution",
        description: "Clear processes exist for users to challenge assessment results or report concerns about institutional misuse."
      }
    ]
  }
];

// Governance Bodies
const GOVERNANCE_BODIES = [
  {
    name: "WII Standards Committee",
    role: "Maintains assessment methodology, scoring standards, and benchmark integrity",
    icon: Scale
  },
  {
    name: "Ethics Review Board",
    role: "Oversees data privacy, consent protocols, and ethical AI use",
    icon: HeartHandshake
  },
  {
    name: "AI Validation Council",
    role: "Reviews and certifies AI tools for Registry inclusion",
    icon: Brain
  },
  {
    name: "Institutional Compliance Office",
    role: "Audits organizational deployments and handles dispute resolution",
    icon: ClipboardCheck
  }
];

// Compliance Levels
const COMPLIANCE_LEVELS = [
  {
    level: "Individual",
    description: "Personal WII assessment and development tracking",
    requirements: ["Consent acknowledgment", "Data access rights", "Privacy protections"],
    badge: "User"
  },
  {
    level: "Organization",
    description: "Enterprise deployment for workforce development",
    requirements: ["Accreditation certification", "Usage policy documentation", "Annual compliance audit", "Employee rights notification"],
    badge: "Enterprise"
  },
  {
    level: "Institutional",
    description: "Government or sector-wide implementation",
    requirements: ["Full governance adoption", "Regulatory alignment", "Public transparency reports", "Independent oversight"],
    badge: "Government"
  }
];

export default function GovernanceFramework() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="governance-framework">
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1E3A8A] via-[#1E3A8A] to-[#2563EB] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="flex items-center gap-2 mb-6">
            <Link to="/research" className="text-sm text-white/60 hover:text-white">
              Research
            </Link>
            <span className="text-white/40">/</span>
            <span className="text-sm text-white font-medium">Governance Framework</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-8 h-8" />
              <h1 className="font-heading text-3xl sm:text-4xl font-bold">
                Workforce Intelligence Governance Framework
              </h1>
            </div>
            <p className="text-lg text-white/80 max-w-3xl mb-4">
              Ethical standards, measurement protocols, and institutional guidelines for 
              responsible Workforce Intelligence Index deployment.
            </p>
            <div className="flex flex-wrap gap-3">
              <Badge className="bg-white/20 text-white">Version 1.0</Badge>
              <Badge className="bg-white/20 text-white">Last Updated: March 2026</Badge>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Purpose Statement */}
        <section className="mb-16">
          <Card className="border-[#E5E7EB] bg-white">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#991B1B]/10 flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-[#991B1B]" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold mb-2" style={{ color: '#991B1B' }}>
                    Purpose & Scope
                  </h2>
                  <p className="text-[#1E3A8A]/80 leading-relaxed">
                    This Governance Framework establishes the ethical, methodological, and institutional 
                    standards for the Workforce Intelligence Index (WII) and its application across 
                    individual, organizational, and institutional contexts. It ensures that workforce 
                    intelligence measurement serves human development goals while protecting user rights 
                    and maintaining scientific integrity.
                  </p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="text-center p-4 rounded-lg bg-[#F8FAFC]">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-[#1E3A8A] mb-1">Development Focus</h4>
                  <p className="text-sm text-[#1E3A8A]/60">WII is a development tool, not a selection filter</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-[#F8FAFC]">
                  <Eye className="w-8 h-8 text-[#1E3A8A] mx-auto mb-2" />
                  <h4 className="font-semibold text-[#1E3A8A] mb-1">Transparency</h4>
                  <p className="text-sm text-[#1E3A8A]/60">Clear methodology and explainable recommendations</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-[#F8FAFC]">
                  <Lock className="w-8 h-8 text-[#991B1B] mx-auto mb-2" />
                  <h4 className="font-semibold text-[#1E3A8A] mb-1">User Rights</h4>
                  <p className="text-sm text-[#1E3A8A]/60">Privacy, consent, and data ownership protected</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Four Pillars */}
        <section className="mb-16" data-testid="governance-pillars">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8 text-center" style={{ color: '#991B1B' }}>
            Four Pillars of Governance
          </h2>
          
          <div className="space-y-6">
            {GOVERNANCE_PILLARS.map((pillar, index) => (
              <motion.div
                key={pillar.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="border-[#E5E7EB] hover:shadow-lg transition-shadow overflow-hidden">
                  <div className="h-1" style={{ backgroundColor: pillar.color }}></div>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${pillar.color}15` }}
                      >
                        <pillar.icon className="w-6 h-6" style={{ color: pillar.color }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-xl font-bold mb-1" style={{ color: pillar.color }}>
                          {pillar.title}
                        </h3>
                        <p className="text-[#1E3A8A]/70 text-sm">{pillar.description}</p>
                      </div>
                    </div>
                    
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value={pillar.id} className="border-none">
                        <AccordionTrigger className="text-sm text-[#1E3A8A] hover:no-underline py-2">
                          View Principles ({pillar.principles.length})
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="grid md:grid-cols-2 gap-4 pt-2">
                            {pillar.principles.map((principle, pIndex) => (
                              <div 
                                key={pIndex} 
                                className="p-4 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB]"
                              >
                                <h4 className="font-semibold text-[#1E3A8A] text-sm mb-2 flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4" style={{ color: pillar.color }} />
                                  {principle.title}
                                </h4>
                                <p className="text-xs text-[#1E3A8A]/60 leading-relaxed">
                                  {principle.description}
                                </p>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Governance Bodies */}
        <section className="mb-16" data-testid="governance-bodies">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8 text-center" style={{ color: '#991B1B' }}>
            Governance Bodies
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {GOVERNANCE_BODIES.map((body, index) => (
              <motion.div
                key={body.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full border-[#E5E7EB] text-center hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center mx-auto mb-4">
                      <body.icon className="w-7 h-7 text-[#1E3A8A]" />
                    </div>
                    <h3 className="font-heading font-bold text-[#1E3A8A] mb-2">{body.name}</h3>
                    <p className="text-sm text-[#1E3A8A]/60">{body.role}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Compliance Levels */}
        <section className="mb-16" data-testid="compliance-levels">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8 text-center" style={{ color: '#991B1B' }}>
            Compliance Requirements by Deployment Level
          </h2>
          
          <div className="grid lg:grid-cols-3 gap-6">
            {COMPLIANCE_LEVELS.map((level, index) => (
              <motion.div
                key={level.level}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <Card className="h-full border-[#E5E7EB] hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <Badge 
                        variant="outline" 
                        className="text-[#1E3A8A] border-[#1E3A8A]"
                      >
                        {level.badge}
                      </Badge>
                      <Layers className="w-5 h-5 text-[#991B1B]" />
                    </div>
                    <h3 className="font-heading text-xl font-bold mb-2" style={{ color: '#991B1B' }}>
                      {level.level} Level
                    </h3>
                    <p className="text-sm text-[#1E3A8A]/70 mb-4">{level.description}</p>
                    
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-[#1E3A8A]/50 uppercase tracking-wide">
                        Requirements
                      </p>
                      {level.requirements.map((req, rIndex) => (
                        <div key={rIndex} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-[#1E3A8A]/70">{req}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Implementation Note */}
        <section className="mb-16">
          <Card className="border-[#E5E7EB] bg-[#1E3A8A]/5">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#991B1B] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-heading font-bold text-[#1E3A8A] mb-2">
                    Implementation Note
                  </h3>
                  <p className="text-sm text-[#1E3A8A]/70 mb-4">
                    This Governance Framework is a living document that will evolve as the Workforce 
                    Intelligence Index expands and new use cases emerge. All stakeholders—individuals, 
                    organizations, and institutions—are encouraged to participate in governance 
                    development through the Research Advisory process.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/research">
                      <Button variant="outline" size="sm" className="text-[#1E3A8A] border-[#1E3A8A]">
                        <BookOpen className="w-4 h-4 mr-2" />
                        View Research
                      </Button>
                    </Link>
                    <Link to="/contact">
                      <Button variant="outline" size="sm" className="text-[#1E3A8A] border-[#1E3A8A]">
                        <Users className="w-4 h-4 mr-2" />
                        Join Advisory
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <section className="text-center">
          <div className="bg-[#991B1B] rounded-2xl p-8 text-white">
            <Award className="w-12 h-12 mx-auto mb-4 opacity-80" />
            <h2 className="font-heading text-2xl font-bold mb-4">
              Committed to Ethical Workforce Intelligence
            </h2>
            <p className="text-white/80 mb-6 max-w-lg mx-auto">
              Explore how WII is designed to support human development with transparency, 
              privacy, and scientific integrity at its core.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/observatories/global/survey">
                <Button size="lg" className="bg-white text-[#991B1B] hover:bg-white/90">
                  Take Assessment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/observatories/workforce-dashboard">
                <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10">
                  View Benchmarks
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
